/**
 * Vuelta de Google o Apple.
 *
 * El servidor hace todo el viaje al proveedor y deja aquí al navegador con el
 * resultado en la query (`status` o `error`, `provider`, `purpose` y
 * `redirect`). Lo que haga falta para seguir (la sesión o el alta pendiente)
 * está en una cookie `httpOnly` que solo lee el servidor, nunca en la URL.
 *
 * - `ticket`: se canjea. Con la identidad ya vinculada se entra; con una nueva
 *   se pide el código de invitación y el nombre para crear la cuenta.
 * - `linked` y `reauthenticated` vuelven a Ajustes.
 * - `error`: se explica y se ofrece volver.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { SOCIAL_PROVIDER_LABEL, parseSocialLanding, type SocialLanding } from '@fitness/core';
import type { PendingSocialSignup } from '@fitness/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Field, fieldAria } from '@/components/Field';
import { Input } from '@/components/Input';
import { ProviderLogo } from '@/components/SocialButtons';
import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { applyFieldErrors, explainError } from '@/lib/errors';
import { queryKeys } from '@/lib/queryClient';
import { HOME_ROUTE, safeRedirect } from '@/lib/redirect';
import { showToast } from '@/lib/toast';

type LandingSearch = Partial<
  Record<'status' | 'error' | 'provider' | 'purpose' | 'redirect', string>
>;

export const Route = createFileRoute('/auth/social')({
  validateSearch: (search: Record<string, unknown>): LandingSearch => {
    const out: LandingSearch = {};
    for (const key of ['status', 'error', 'provider', 'purpose', 'redirect'] as const) {
      const value = search[key];
      if (typeof value === 'string') out[key] = value;
    }
    return out;
  },
  component: SocialLandingPage,
});

function SocialLandingPage() {
  const search = Route.useSearch();
  const landing = parseSocialLanding((key) => search[key as keyof LandingSearch] ?? null);

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-app flex-col justify-center gap-6 p-6">
      {landing.kind === 'ticket' ? <TicketLanding landing={landing} /> : null}
      {landing.kind === 'linked' || landing.kind === 'reauthenticated' ? (
        <BackToSettings landing={landing} />
      ) : null}
      {landing.kind === 'link_pending' ? <ConfirmLink landing={landing} /> : null}
      {landing.kind === 'error' ? <ErrorLanding landing={landing} /> : null}
    </main>
  );
}

// --- Errores -------------------------------------------------------------

function ErrorLanding({ landing }: { landing: Extract<SocialLanding, { kind: 'error' }> }) {
  const { status } = useAuth();
  const navigate = useNavigate();
  // Vincular y reautenticar se empiezan en Ajustes, con la sesión abierta.
  const fromSettings = landing.purpose !== 'login' && status === 'authenticated';
  return (
    <>
      <Header title="No se ha podido continuar" />
      <ErrorBanner title="Algo no ha ido bien" message={landing.message} />
      {fromSettings ? (
        <Button
          variant="secondary"
          onClick={() => void navigate({ href: safeRedirect(landing.redirect), replace: true })}
        >
          Volver a Ajustes
        </Button>
      ) : (
        <Link
          to="/login"
          className="text-center text-sm font-semibold text-brand underline-offset-4 hover:underline"
        >
          Volver a iniciar sesión
        </Link>
      )}
    </>
  );
}

// --- Vincular / reautenticar -----------------------------------------------

function BackToSettings({
  landing,
}: {
  landing: Extract<SocialLanding, { kind: 'linked' | 'reauthenticated' }>;
}) {
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    if (landing.kind === 'linked' && landing.provider !== null) {
      showToast(`${SOCIAL_PROVIDER_LABEL[landing.provider]} vinculado. Ya puedes entrar con él.`);
    }
    void navigate({ href: safeRedirect(landing.redirect), replace: true });
  }, [landing, navigate]);
  return <Spinner label="Volviendo" />;
}

/**
 * La vinculación solo se hace al confirmarla con la sesión de la cuenta que
 * la preparó: un enlace de vinculación abierto en otro navegador no vincula
 * nada.
 */
function ConfirmLink({ landing }: { landing: Extract<SocialLanding, { kind: 'link_pending' }> }) {
  const { status } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);
  const started = useRef(false);
  const provider = landing.provider;
  // Sin la sesión de la cuenta que empezó la vinculación no hay nada que
  // confirmar (el servidor lo rechazaría igualmente).
  const canConfirm = status === 'authenticated' && provider !== null;
  const error = canConfirm
    ? failure
    : 'Para vincular un proveedor, entra en tu cuenta y empieza desde Ajustes → Cuenta en este mismo navegador.';

  useEffect(() => {
    if (started.current || !canConfirm || provider === null) return;
    started.current = true;
    api.me
      .confirmLink(provider)
      .then(async () => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.loginMethods });
        showToast(`${SOCIAL_PROVIDER_LABEL[provider]} vinculado. Ya puedes entrar con él.`);
        await navigate({ href: safeRedirect(landing.redirect), replace: true });
      })
      .catch((cause: unknown) => setFailure(explainError(cause).message));
  }, [canConfirm, provider, landing.redirect, navigate, queryClient]);

  if (error === null) return <Spinner label="Vinculando" />;
  return (
    <>
      <Header title="No se ha podido vincular" />
      <ErrorBanner title="Algo no ha ido bien" message={error} />
      <Link
        to="/settings/account"
        className="text-center text-sm font-semibold text-brand underline-offset-4 hover:underline"
      >
        Volver a Ajustes
      </Link>
    </>
  );
}

// --- Entrar o crear la cuenta ----------------------------------------------

type TicketState =
  | { kind: 'loading' }
  | { kind: 'signup'; signup: PendingSocialSignup }
  | { kind: 'error'; message: string };

function TicketLanding({ landing }: { landing: Extract<SocialLanding, { kind: 'ticket' }> }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<TicketState>({ kind: 'loading' });
  // El *ticket* se gasta al canjearlo: en desarrollo, StrictMode monta el
  // efecto dos veces y la segunda vería el ticket ya usado.
  const started = useRef(false);
  const destination = landing.redirect === '/' ? HOME_ROUTE : safeRedirect(landing.redirect);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    auth
      .completeSocialLogin()
      .then(async (result) => {
        if (result.kind === 'signed_in') {
          await navigate({ href: destination, replace: true });
          return;
        }
        setState({ kind: 'signup', signup: result.signup });
      })
      .catch((error: unknown) => setState({ kind: 'error', message: explainError(error).message }));
  }, [auth, navigate, destination]);

  if (state.kind === 'loading') {
    return (
      <div className="flex flex-col items-center gap-3 text-sm text-ink-muted">
        <Spinner label="Entrando" />
        Entrando…
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <>
        <Header title="No se ha podido entrar" />
        <ErrorBanner title="Algo no ha ido bien" message={state.message} />
        <Link
          to="/login"
          className="text-center text-sm font-semibold text-brand underline-offset-4 hover:underline"
        >
          Volver a iniciar sesión
        </Link>
      </>
    );
  }
  return <SignupForm signup={state.signup} destination={destination} />;
}

const signupFormSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(1, 'Escribe cómo quieres que te llamemos.')
    .max(60, 'Como mucho 60 caracteres.'),
  inviteCode: z.string().trim().max(128, 'Como mucho 128 caracteres.'),
});

type SignupFormValues = z.infer<typeof signupFormSchema>;

function SignupForm({ signup, destination }: { signup: PendingSocialSignup; destination: string }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const provider = SOCIAL_PROVIDER_LABEL[signup.provider];

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: { displayName: signup.displayName, inviteCode: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await auth.socialSignup({ displayName: values.displayName, inviteCode: values.inviteCode });
      await navigate({ href: destination, replace: true });
    } catch (error) {
      const explained = explainError(error);
      if (applyFieldErrors(explained, ['displayName', 'inviteCode'] as const, setError)) return;
      setFormError(explained.message);
    }
  });

  return (
    <>
      <Header
        title="Crea tu cuenta"
        subtitle={`Es la primera vez que entras con ${provider}. Solo falta el código de invitación.`}
      />
      <p className="flex items-center justify-center gap-2 rounded-xl border border-line bg-surface-raised p-3 text-sm">
        <ProviderLogo provider={signup.provider} className="size-4 shrink-0" />
        <span className="truncate">{signup.email}</span>
      </p>

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError ? (
          <ErrorBanner title="No se ha podido crear la cuenta" message={formError} />
        ) : null}

        <Field htmlFor="displayName" label="Nombre" error={errors.displayName?.message}>
          <Input
            {...fieldAria('displayName', errors.displayName?.message)}
            type="text"
            autoComplete="name"
            {...register('displayName')}
          />
        </Field>

        <Field
          htmlFor="inviteCode"
          label="Código de invitación"
          error={errors.inviteCode?.message}
          hint="Te lo ha pasado quien te invitó a la app."
        >
          <Input
            {...fieldAria('inviteCode', errors.inviteCode?.message, true)}
            type="text"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            {...register('inviteCode')}
          />
        </Field>

        <Button type="submit" size="lg" block loading={isSubmitting}>
          Crear cuenta
        </Button>
      </form>

      <p className="text-center text-xs text-ink-muted">
        Tu cuenta no tendrá contraseña: entrarás con {provider}. Puedes ponerle una cuando quieras
        en Ajustes → Cuenta.
      </p>
    </>
  );
}

function Header({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <header className="text-center">
      <img src="/icons/icon-192.png" alt="" className="mx-auto size-16 rounded-2xl" />
      <h1 className="mt-4 text-2xl font-bold">{title}</h1>
      {subtitle ? <p className="mt-1 text-sm text-ink-muted">{subtitle}</p> : null}
    </header>
  );
}
